import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BaseDetails from 'containers/Base/BaseDetails.js';
import { closeDetails, openDetails, toggleModal } from 'reducers/ui';
import staticText from 'static/text_display.json';
import { translatedKeyValueText } from 'utils/methods/helpers';
import PropTypes from 'prop-types';
import { getPatrolTemplateDetailTransformed } from 'containers/Pages/Tasks/PatrolTemplates/Selectors/patrolTemplateSelectors';
import { actions as patrolTemplateActions } from 'containers/Pages/Tasks/PatrolTemplates/Modules/dataGridTable';
import { nameSpace } from 'containers/Pages/Tasks/PatrolTemplates/Modules/nameSpace';
import { PAGE_URLS } from 'utils/constants/';

const PatrolTemplateDetails = ({
  id,
  editable,
  toggleEditMode,
  cancel,
  downloadable = true,
}) => {
  //------------------------------Selectors---------------------------------
  const details = useSelector(state =>
    getPatrolTemplateDetailTransformed(state),
  );
  const loading = useSelector(state => state.ui.loading);
  //-----------------------Actions------------------------------------------
  const dispatch = useDispatch();
  const dispatchFetchPatrolTemplateDetails = useCallback(
    id => dispatch(patrolTemplateActions.fetchGridRowDetails({ id })),
    [dispatch],
  );
  const dispatchClearPatrolTemplateDetails = useCallback(
    () => dispatch(patrolTemplateActions.setGridRowDetails({ details: {} })),
    [dispatch],
  );
  const dispatchSetPatrolTemplateID = useCallback(
    id => {
      dispatch(patrolTemplateActions.setSelectedRowID(id));
    },
    [dispatch],
  );
  const dispatchToggleModal = useCallback(
    nameSpace => dispatch(toggleModal(nameSpace)),
    [dispatch],
  );
  const dispatchOpenDetails = useCallback(
    url => {
      dispatch(openDetails(url));
    },
    [dispatch],
  );
  const dispatchCloseDetails = useCallback(
    url => {
      dispatch(closeDetails(url));
    },
    [dispatch],
  );
  const dispatchFetchCleaningTaskDetailsPdf = useCallback(
    id => {
      dispatch(patrolTemplateActions.fetchGridRowDetailsPdf({ id }));
    },
    [dispatch],
  );
  //----------------------------hooks-------------------------------------------
  useEffect(() => {
    if (id) {
      dispatchFetchPatrolTemplateDetails(id);
      dispatchSetPatrolTemplateID(id);
      dispatchOpenDetails(PAGE_URLS.TEMPLATE_PATROLS.link);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  //-------------------------------------
  const onEdit = () => {
    toggleEditMode();
    dispatchToggleModal(nameSpace);
  };
  const cancelDetails = () => {
    cancel();
    dispatchClearPatrolTemplateDetails();
    dispatchCloseDetails(PAGE_URLS.TEMPLATE_PATROLS.link);
  };
  const getTitleValue = type => {
    if (Object.keys(details).length < 1) {
      return type === staticText.general.edit
        ? staticText.managerial_view.patrol.edit_default
        : staticText.managerial_view.patrol.download_default;
    }

    return details.generalInfo.patrolName.value;
  };

  const onDownloadClick = () => {
    dispatchFetchCleaningTaskDetailsPdf(id);
  };

  return (
    <BaseDetails
      header={staticText.managerial_view.patrol.details}
      cancel={cancelDetails}
      editable={editable}
      onEdit={onEdit}
      details={details}
      loading={loading}
      editHoverText={translatedKeyValueText('edit_value_title', {
        value: getTitleValue(staticText.general.edit),
      })}
      downloadable={downloadable}
      {...(downloadable && {
        onDownloadClick: onDownloadClick,
        downloadHoverText: staticText.reporting_view.task_download,
      })}
      downloadHoverText={translatedKeyValueText('download_report_operational', {
        value: getTitleValue(staticText.general.download),
      })}
    />
  );
};
PatrolTemplateDetails.propTypes = {
  cancel: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  id: PropTypes.string,
  loading: PropTypes.bool,
  downloadable: PropTypes.bool,
  toggleEditMode: PropTypes.func,
};

export default PatrolTemplateDetails;
